import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function Section({ number, title, children }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-400/10 border border-orange-400/30 flex items-center justify-center text-orange-400 text-sm font-medium">
        {number}
      </div>
      <div className="flex-1 pb-8">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function PresalesGuide() {
  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/guide" className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Guide
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8">Launching Presales with VSC-LAUNCH</h1>

      <p className="text-gray-400 leading-relaxed mb-10">
        VSC-LAUNCH is Vextaris's native presale system with built-in anti-rug protection. Tokens are
        escrowed on-chain, LP is auto-locked, and contributors can claim refunds if the soft cap is
        not met. No third-party launchpad contracts — it is all protocol-level.
      </p>

      <div className="space-y-2">
        <Section number={1} title="Create a token first">
          <p>
            You need an existing VSC-7 token to run a presale. If you have not created one yet,
            follow the <Link to="/guide/tokens" className="text-vextaris-blue hover:underline">Token creation guide</Link> first.
            You will need the token's <span className="text-white">mint address</span> for the presale configuration.
          </p>
        </Section>

        <Section number={2} title="Navigate to Presales">
          <p>
            Go to the <Link to="/presales" className="text-vextaris-blue hover:underline">Presales</Link> page
            from the sidebar, or use the <span className="text-white">Deploy &gt; Presale</span> template
            for a guided form.
          </p>
        </Section>

        <Section number={3} title="Configure the presale">
          <p>
            Set the core parameters:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li><span className="text-white">Token allocation</span> — how many tokens to sell in the presale</li>
            <li><span className="text-white">Price per token</span> — cost in VXS per token unit</li>
            <li><span className="text-white">Min contribution</span> — minimum VXS per participant</li>
            <li><span className="text-white">Max contribution</span> — maximum VXS per participant (prevents whale domination)</li>
            <li><span className="text-white">Soft cap</span> — minimum VXS raised for the presale to succeed</li>
            <li><span className="text-white">Hard cap</span> — maximum VXS raised (presale closes when hit)</li>
          </ul>
        </Section>

        <Section number={4} title="Set timing">
          <p>
            Define the <span className="text-white">start time</span> and <span className="text-white">end time</span> for
            the presale window. Contributions are only accepted during this period. Plan enough time
            for your community to participate, but not so long that momentum stalls.
          </p>
        </Section>

        <Section number={5} title="Configure LP settings">
          <p>
            Specify the <span className="text-white">LP percentage</span> — the portion of raised VXS
            that will be paired with tokens to create a DEX liquidity pool at finalization.
          </p>
          <p>
            Set the <span className="text-white">LP lock duration</span> — how long the liquidity is locked
            after the pool is created. Longer lock periods significantly increase the token's reputation
            score. This is the core anti-rug mechanism: locked liquidity cannot be withdrawn by the creator.
          </p>
        </Section>

        <Section number={6} title="Deploy the presale">
          <p>
            Review the configuration summary, connect your wallet, and sign the transaction. At this point,
            the token allocation is <span className="text-white">escrowed on-chain</span> — the tokens are
            held by the protocol, not in your wallet. This guarantees they will be distributed to
            contributors or returned to you if the presale fails.
          </p>
        </Section>

        <Section number={7} title="Contributors buy during the window">
          <p>
            Between the start and end times, participants can contribute VXS up to the max contribution
            limit. Contributions are tracked on-chain. The presale page on VexForge and VexScan shows
            real-time progress toward the soft and hard caps.
          </p>
        </Section>

        <Section number={8} title="Finalize (if soft cap met)">
          <p>
            After the end time, if the soft cap was reached, anyone can call <span className="text-white">finalize</span>.
            Finalization triggers three actions atomically:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li>Tokens are distributed to contributors proportional to their VXS contribution</li>
            <li>The configured LP percentage of VXS + tokens creates a DEX liquidity pool</li>
            <li>The LP tokens are locked for the specified duration</li>
          </ul>
          <p>
            Remaining VXS (after LP creation) is sent to the presale creator.
          </p>
        </Section>

        <Section number={9} title="Refunds (if soft cap not met)">
          <p>
            If the presale ends without reaching the soft cap, the presale is considered failed.
            Contributors can <span className="text-white">claim refunds</span> to get their VXS back.
            The escrowed tokens are returned to the creator. No funds are lost.
          </p>
          <p>
            The creator can also <span className="text-white">cancel</span> a presale before it starts
            (or during the window if no contributions have been made). Cancellation returns all
            escrowed tokens immediately.
          </p>
        </Section>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-3">Tips</h3>
        <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
          <li>Longer LP lock duration = higher token reputation. <span className="text-white">90+ days</span> is a strong trust signal.</li>
          <li>Set a realistic soft cap. If it is too high, you risk a failed presale and wasted marketing effort.</li>
          <li>Anti-rug protection is automatic — nobody (including the creator) can pull locked liquidity.</li>
          <li>Cancelled presales return all escrowed tokens to the creator with no penalty.</li>
          <li>Max contribution limits prevent single wallets from taking a disproportionate share.</li>
          <li>The presale is visible on VexScan as soon as it is deployed. Share the link with your community.</li>
        </ul>
      </div>

      {/* Docs link */}
      <div className="mt-6 text-sm text-gray-500">
        For the full presale specification, see{' '}
        <a href="https://docs.vextaris.io/token-standards/overview#protocol-standards" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">
          docs.vextaris.io/token-standards/overview#protocol-standards
        </a>.
      </div>
    </div>
  );
}
